import { consola } from 'consola'
import { useAuthAnalytics } from './useAuthAnalytics'
import { useUserProfileStore } from '@entities/user'
import { AuthLogoutService, LogoutReason, UserType } from '~/features/auth/lib/AuthLogoutService'

const logger = consola.withTag('logout-handler')
const MAX_REFRESH_FAILURES = 2

let refreshTokenFailureCount = 0
let isRefreshingToken = false

export const useLogoutHandler = () => {
  useUserProfileStore()

  const handleRefreshTokenExpired = async (): Promise<void> => {
    if (AuthLogoutService.isLoggingOut()) return
    await AuthLogoutService.logout({
      reason: LogoutReason.REFRESH_TOKEN_EXPIRED,
      userType: UserType.REGULAR,
      silent: true,
      metadata: {
        refreshTokenFailureCount,
        source: 'features/auth/model/useLogoutHandler',
      },
    })
  }

  const handleInvalidRefreshToken = (): void => {
    if (process.client) localStorage.setItem('refresh_token_invalid', 'true')
  }

  const handleInvalidRefreshTokenError = (): void => {
    refreshTokenFailureCount++
    if (refreshTokenFailureCount >= MAX_REFRESH_FAILURES) {
      logger.error('Refresh token failure limit reached')
      void handleRefreshTokenExpired()
    }
  }

  const incrementRefreshTokenFailureCount = (): number => ++refreshTokenFailureCount
  const resetRefreshTokenFailureCount = (): void => { refreshTokenFailureCount = 0 }
  const getRefreshTokenFailureCount = (): number => refreshTokenFailureCount
  const setRefreshingToken = (value: boolean): void => { isRefreshingToken = value }
  const isTokenRefreshing = (): boolean => isRefreshingToken

  const isRefreshTokenInvalid = (): boolean => {
    if (!process.client) return false
    return localStorage.getItem('refresh_token_invalid') === 'true'
  }

  const clearInvalidFlag = (): void => {
    if (process.client) localStorage.removeItem('refresh_token_invalid')
  }

  const handleSuccessfulLogin = (): void => {
    clearInvalidFlag()
    resetRefreshTokenFailureCount()

    // Единственная точка, куда сходятся все шесть входов (пароль, VK, MAX, Telegram,
    // Яндекс, MAX WebApp) — поэтому финал воронки авторизации отмечается здесь, а не
    // в шести местах. Способ входа берётся из начатой попытки: восстановление сессии
    // сюда тоже приходит, но попытки за ним нет, и событие не отправляется.
    try {
      useAuthAnalytics().trackAuthSuccess()
    } catch (error) {
      logger.debug('Не удалось отметить успешный вход в аналитике', error)
    }
  }

  /**
   * Приводит локальный учёт токенов в исходное состояние после разлогина,
   * чтобы следующий вход в этой вкладке начинался с чистого счётчика неудач.
   *
   * Метод звал плагин синхронизации разлогина между вкладками, но его не существовало:
   * вызов падал с TypeError уже после очистки сессии, и редирект следом не выполнялся —
   * вкладка оставалась на странице личного кабинета с пустой сессией.
   */
  const handleSuccessfulLogout = (): void => {
    clearInvalidFlag()
    resetRefreshTokenFailureCount()
    setRefreshingToken(false)
  }

  const resetRedirectFlag = (): void => {}

  return {
    handleRefreshTokenExpired,
    handleInvalidRefreshToken,
    handleInvalidRefreshTokenError,
    incrementRefreshTokenFailureCount,
    resetRefreshTokenFailureCount,
    getRefreshTokenFailureCount,
    setRefreshingToken,
    isTokenRefreshing,
    isRefreshTokenInvalid,
    clearInvalidFlag,
    handleSuccessfulLogin,
    handleSuccessfulLogout,
    resetRedirectFlag,
  }
}
